/**
 * AnchorResolver — resuelve la posición de pantalla {x, y, depth} para cada
 * Anchor de un emitter activo, llamado cada frame por ParticleManager.
 *
 * El AnchorResolver no conoce GameScene directamente: recibe getters de
 * entidades y del tiempo actual para mantenerse desacoplado y testeable.
 */

import type Phaser from 'phaser';

const TILE_SIZE = 32;

// ────────────────────────────────────────────────────────────────────────────
// Tipos públicos
// ────────────────────────────────────────────────────────────────────────────

/** Slots anclables sobre una entidad (player o NPC). */
export type EntityAnchorSlot =
  | 'feet'         // base del body (centro inferior)
  | 'center'       // centro del body
  | 'head'         // centro de la cabeza
  | 'above'        // encima de la cabeza (~16px)
  | 'weapon_tip'   // punta del arma según dirección
  | 'weapon_hilt'  // empuñadura del arma
  | 'shield_front' // frente del escudo
  | 'helm_top';    // tope del casco

export type Anchor =
  | { kind: 'world'; x: number; y: number; depth?: number }
  | { kind: 'tile'; tx: number; ty: number; depth?: number }
  | {
      kind: 'entity';
      entityId: string;
      slot: EntityAnchorSlot;
      offsetX?: number;
      offsetY?: number;
    }
  | { kind: 'item_ground'; itemInstanceId: string }
  | {
      kind: 'projectile';
      from: Anchor;
      to: Anchor;
      durationMs: number;
      startedAt: number;
      /** Altura del arco parabólico en px. 0 = línea recta. */
      arc?: number;
    };

export interface ResolvedAnchor {
  x: number;
  y: number;
  depth: number;
  /** false si el anchor no se pudo resolver (entidad desapareció, etc.). */
  valid: boolean;
}

/**
 * Subset estructural que tanto PlayerRenderObjects como NPCRenderObjects
 * satisfacen. AnchorResolver depende solo de esto para mantenerse desacoplado.
 */
export interface EntityRenderable {
  sprite: Phaser.GameObjects.Sprite;
  head: Phaser.GameObjects.Sprite | null;
  weapon: Phaser.GameObjects.Sprite | null;
  shield: Phaser.GameObjects.Sprite | null;
  /** Player tiene helm; NPC no. Default null. */
  helm?: Phaser.GameObjects.Sprite | null;
}

/** Sprite/registro de un item en el piso (drop). */
export interface ItemGroundRenderable {
  sprite: Phaser.GameObjects.Sprite;
}

/**
 * Acceso a los maps de render del juego. ParticleManager pasa estos getters
 * en construcción, AnchorResolver los consulta cuando lo necesita.
 */
export interface AnchorResolverContext {
  getEntity(entityId: string): EntityRenderable | undefined;
  getItemGround(itemInstanceId: string): ItemGroundRenderable | undefined;
  /** Tiempo actual en ms (typicamente performance.now() o scene.time.now). */
  now(): number;
}

// ────────────────────────────────────────────────────────────────────────────
// Implementación
// ────────────────────────────────────────────────────────────────────────────

const INVALID: ResolvedAnchor = { x: 0, y: 0, depth: 0, valid: false };

export class AnchorResolver {
  constructor(private readonly ctx: AnchorResolverContext) {}

  resolve(anchor: Anchor): ResolvedAnchor {
    switch (anchor.kind) {
      case 'world':
        return {
          x: anchor.x,
          y: anchor.y,
          depth: anchor.depth ?? 10000,
          valid: true,
        };

      case 'tile':
        return {
          x: anchor.tx * TILE_SIZE + TILE_SIZE / 2,
          y: anchor.ty * TILE_SIZE + TILE_SIZE / 2,
          depth: anchor.depth ?? 10,
          valid: true,
        };

      case 'entity':
        return this.resolveEntity(anchor);

      case 'item_ground':
        return this.resolveItemGround(anchor.itemInstanceId);

      case 'projectile':
        return this.resolveProjectile(anchor);
    }
  }

  // ── Entity ────────────────────────────────────────────────────────────────

  private resolveEntity(
    anchor: Extract<Anchor, { kind: 'entity' }>
  ): ResolvedAnchor {
    const entity = this.ctx.getEntity(anchor.entityId);
    if (!entity) return INVALID;

    const offsetX = anchor.offsetX ?? 0;
    const offsetY = anchor.offsetY ?? 0;
    const baseDepth = entity.sprite.depth;

    let x = entity.sprite.x + offsetX;
    let y = entity.sprite.y + offsetY;
    let depth = baseDepth + 100;

    switch (anchor.slot) {
      case 'feet':
        // sprite tiene origin (0.5, 1), su y ES los pies
        y += 0;
        depth = baseDepth - 1; // debajo del personaje
        break;

      case 'center':
        y -= entity.sprite.displayHeight / 2;
        break;

      case 'head':
        if (entity.head) {
          x = entity.head.x + offsetX;
          y = entity.head.y + offsetY;
        } else {
          y -= entity.sprite.displayHeight * 0.85;
        }
        break;

      case 'above':
        y -= entity.sprite.displayHeight + 16;
        break;

      case 'weapon_tip':
      case 'weapon_hilt':
        if (entity.weapon) {
          // El sprite del arma tiene origin (0.5, 1) y se posiciona en
          // bodyBottomY (los pies del char). La punta/empuñadura cae
          // en distinto lugar según la dirección del frame actual.
          //
          // Convención de frames (WeaponSpriteManager.ts):
          //   row 0 = DOWN, 1 = UP, 2 = LEFT, 3 = RIGHT
          // Para conocer la dir leemos el nombre del frame `r{row}c{col}`.
          const w = entity.weapon.displayWidth;
          const h = entity.weapon.displayHeight;
          const frameName = String(entity.weapon.frame?.name ?? '');
          const dirRow = frameName.startsWith('r') ? Number(frameName[1]) : 0;

          let dx = 0;
          let dy = 0;
          if (anchor.slot === 'weapon_tip') {
            // Punta del arma — heurísticas por dirección. El frame del
            // arma (42x64 para weapon1) es MÁS ALTO que el body, así
            // que offsets grandes en Y quedan por encima de la cabeza.
            // El gráfico real de la espada vive a nivel mano/pecho del
            // char, alrededor de la mitad-baja del frame.
            switch (dirRow) {
              case 0:  // DOWN — tip hacia el lado izquierdo del char
                dx = -w * 0.15; dy = -h * 0.3;
                break;
              case 1:  // UP — tip hacia el lado derecho del char
                dx = w * 0.15; dy = -h * 0.3;
                break;
              case 2:  // LEFT — tip a la izquierda
                dx = -w * 0.3; dy = -h * 0.28;
                break;
              case 3:  // RIGHT — tip a la derecha
              default:
                dx = w * 0.3; dy = -h * 0.28;
                break;
            }
          } else {
            // weapon_hilt — empuñadura, ~mitad del frame.
            dy = -h * 0.5;
          }

          x = entity.weapon.x + dx + offsetX;
          y = entity.weapon.y + dy + offsetY;

          // Hereda la depth del arma con delta mínimo, así el FX respeta
          // el front/back direccional que ya setea GameScene
          // (frontDelta = +0.02 para DOWN/RIGHT, backDelta = -0.02 para
          // UP/LEFT). Un +1 fijo rompía ese layering.
          depth = entity.weapon.depth + 0.001;
        } else {
          return INVALID;
        }
        break;

      case 'shield_front':
        if (entity.shield) {
          x = entity.shield.x + offsetX;
          y = entity.shield.y + offsetY;
          depth = entity.shield.depth + 1;
        } else {
          return INVALID;
        }
        break;

      case 'helm_top':
        if (entity.helm) {
          x = entity.helm.x + offsetX;
          y = entity.helm.y - entity.helm.displayHeight / 2 + offsetY;
          depth = entity.helm.depth + 1;
        } else if (entity.head) {
          x = entity.head.x + offsetX;
          y = entity.head.y - entity.head.displayHeight / 2 + offsetY;
        } else {
          return INVALID;
        }
        break;
    }

    return { x, y, depth, valid: true };
  }

  // ── Item ground ───────────────────────────────────────────────────────────

  private resolveItemGround(itemInstanceId: string): ResolvedAnchor {
    const item = this.ctx.getItemGround(itemInstanceId);
    if (!item) return INVALID;
    return {
      x: item.sprite.x,
      y: item.sprite.y,
      depth: item.sprite.depth + 1,
      valid: true,
    };
  }

  // ── Projectile ────────────────────────────────────────────────────────────

  private resolveProjectile(
    anchor: Extract<Anchor, { kind: 'projectile' }>
  ): ResolvedAnchor {
    const from = this.resolve(anchor.from);
    const to = this.resolve(anchor.to);
    if (!from.valid || !to.valid) return INVALID;

    const elapsed = this.ctx.now() - anchor.startedAt;
    const t = Math.max(0, Math.min(1, elapsed / anchor.durationMs));

    // Interpolación lineal entre from y to.
    let x = from.x + (to.x - from.x) * t;
    let y = from.y + (to.y - from.y) * t;

    // Arco parabólico opcional: pico en t=0.5.
    if (anchor.arc && anchor.arc !== 0) {
      const arcOffset = -4 * anchor.arc * t * (1 - t); // peak negativo (hacia arriba)
      y += arcOffset;
    }

    return {
      x,
      y,
      depth: Math.max(from.depth, to.depth) + 50,
      valid: true,
    };
  }
}
